import React from 'react';
import { DoyenBrand } from '../components/brand/DoyenBrand';
import { GoogleLogo } from '../components/logos/GoogleLogo';
import { Box, Users, BookOpen, Shield } from 'lucide-react';

export function About() {
  return (
    <div className="min-h-screen pt-16">
      <section className="relative bg-white px-4 pb-16">
        <div className="max-w-3xl mx-auto pt-12">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Why <DoyenBrand />?
            </h2>
            <p className="text-xl text-gray-700 mb-6">
              Enterprise software acts as the backbone of modern businesses. Tens of billions are spent annually on implementing and integrating enterprise software. Between five and ten billion are spent alone on the core of any business - financial data migrations. Lots of money spent. Few happy outcomes.
            </p>
            <p className="text-xl text-gray-700 mb-6">
              The advent of LLMs, new ML Techniques, and Agents offer an opportunity to rethink how we approach financial data migraitons. We can enable the highest-quality migrations, accelerated by AI, and managed by finance professionals.
            </p>
            <p className="text-xl text-gray-700 mb-6">
              The founding team consists of PhDs, graduates, and former employees from Caltech, Cornell, UC Berkeley, Meta, and Google.
            </p>
            <p className="text-xl text-gray-700 mb-8">
              <DoyenBrand /> is venture-backed by leading Silicon Valley VCs including <GoogleLogo />.
            </p>

            {/* Logos Grid */}
            <div className="max-w-3xl mx-auto">
              {/* Universities Row */}
              <div className="flex justify-center items-center gap-12 mb-8">
                <img 
                  src="/company-logos/Caltech_Logo.png" 
                  alt="Caltech" 
                  className="h-12 object-contain"
                />
                <img 
                  src="/company-logos/Cornell_Big_Red_logo.svg.png" 
                  alt="Cornell University" 
                  className="h-12 object-contain"
                />
                <img 
                  src="/company-logos/UC_Berkeley_Cal_Logo.png" 
                  alt="UC Berkeley" 
                  className="h-12 object-contain"
                />
              </div>
              
              {/* Companies Row */}
              <div className="flex justify-center items-center gap-12">
                <img 
                  src="/company-logos/Meta_Platforms_Inc._logo.svg.png" 
                  alt="Meta" 
                  className="h-8 object-contain"
                />
                <img 
                  src="/company-logos/google_logo.png" 
                  alt="Google" 
                  className="h-8 object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-surface py-24">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-primary text-center mb-16">Our Values</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Think Outside the Box */}
            <div className="bg-card rounded-xl p-8 shadow-sm border border-neutral-200 hover:border-secondary hover:shadow-md transition-all duration-300">
              <div className="h-12 w-12 rounded-lg bg-secondary/10 flex items-center justify-center mb-6">
                <Box className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="text-xl font-bold text-primary mb-3">Think Outside the Box</h3>
              <p className="text-neutral-600">Challenging conventions to find innovative solutions for complex enterprise problems.</p>
            </div>

            {/* Customer-Focused */}
            <div className="bg-card rounded-xl p-8 shadow-sm border border-neutral-200 hover:border-secondary hover:shadow-md transition-all duration-300">
              <div className="h-12 w-12 rounded-lg bg-secondary/10 flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="text-xl font-bold text-primary mb-3">Customer-Focused</h3>
              <p className="text-neutral-600">Putting our customers' success at the heart of everything we do.</p>
            </div>

            {/* Always Learning */}
            <div className="bg-card rounded-xl p-8 shadow-sm border border-neutral-200 hover:border-secondary hover:shadow-md transition-all duration-300">
              <div className="h-12 w-12 rounded-lg bg-secondary/10 flex items-center justify-center mb-6">
                <BookOpen className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="text-xl font-bold text-primary mb-3">Always Learning</h3>
              <p className="text-neutral-600">Continuously expanding our knowledge to stay at the forefront of AI innovation.</p>
            </div>

            {/* Resilient */}
            <div className="bg-card rounded-xl p-8 shadow-sm border border-neutral-200 hover:border-secondary hover:shadow-md transition-all duration-300">
              <div className="h-12 w-12 rounded-lg bg-secondary/10 flex items-center justify-center mb-6">
                <Shield className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="text-xl font-bold text-primary mb-3">Resilient</h3>
              <p className="text-neutral-600">Adapting and persevering through challenges to deliver exceptional results.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-primary mb-6">Join Our Team</h2>
          <p className="text-xl text-neutral-600 mb-8">
            We're always looking for exceptional teammates to join us. If you're passionate about AI, data, enterprise software, and making a real impact, we'd love to hear from you.
          </p>
          <a 
            href="mailto:careers@doyen.com"
            className="inline-flex items-center px-6 py-3 bg-secondary text-white rounded-lg text-lg font-semibold hover:bg-secondary-dark transition-colors"
          >
            Contact Us
          </a>
        </div>
      </section>
    </div>
  );
}